import React from 'react';
import styled from 'styled-components';
import { pluralize } from '../../utils/pluralize';
import { Skeleton } from '../feedback/Skeleton';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { Avatar } from '../media/Avatar';
import { MediaCover } from '../media/MediaCover';
import { ReactComponent as UserCoverSvg } from '../resources/defaultCover.svg';
import { Typography } from '../typography/Typography';
import type { UserUI } from './types';

const Content = styled(Flex).attrs((props) => ({
    ...props,
    direction: { xs: 'column', sm: 'row' },
    spacing: { xs: 1, sm: 0 },
    justifyContent: { sm: 'space-between' },
    alignItems: { sm: 'center' },
    position: 'relative',
    background: 'var(--background-primary)',
    zIndex: 1,
    px: 2,
    py: 1,
}))``;

interface UserCardProps {
    user: UserUI;
    onShowSubscriptions: VoidFunction;
}

export const UserCard = (props: UserCardProps) => {
    const { user, onShowSubscriptions } = props;

    return (
        <Box>
            <MediaCover ratio={16 / 9}>
                <UserCoverSvg />
            </MediaCover>
            <Content>
                <Flex spacing={2} direction="row" alignItems="center">
                    <Avatar src={user?.avatarUrl} title={user?.displayName} size={108} mt={-3} />
                    <Flex direction="column">
                        <Typography variant="title2" weight="bold">
                            {user.displayName}
                        </Typography>
                        {user.login && (
                            <Typography color="secondary" variant="caption">
                                {user.login}
                            </Typography>
                        )}
                    </Flex>
                </Flex>
                {user?.subscriptions > 0 && (
                    <Flex
                        cursor="pointer"
                        direction={{ xs: 'row', sm: 'column' }}
                        alignItems={{ sm: 'flex-end' }}
                        spacing={{ xs: 0.5, sm: 0 }}
                        px={{ sm: 1 }}
                        onClick={onShowSubscriptions}
                    >
                        <Typography variant="body" weight="medium">
                            {user.subscriptions}
                        </Typography>
                        <Typography variant="body" weight="medium">
                            {pluralize(user.subscriptions, 'подписка', 'подписки', 'подписок')}
                        </Typography>
                    </Flex>
                )}
            </Content>
        </Box>
    );
};

export const UserCardSkeleton = () => (
    <Box>
        <Skeleton variant="rect" ratio={16 / 9} />
        <Content>
            <Flex spacing={2} direction="row" alignItems="center">
                <Skeleton variant="circular" width={108} height={108} mt={-3} />
                <Flex direction="column">
                    <Skeleton variant="text" width={200} height={26} />
                    <Skeleton variant="text" width={120} height={14} />
                </Flex>
            </Flex>

            <Flex
                direction={{ xs: 'row', sm: 'column' }}
                alignItems={{ sm: 'flex-end' }}
                spacing={{ xs: 0.5, sm: 0 }}
                px={{ sm: 1 }}
            >
                <Skeleton variant="text" width={60} height={20} />
                <Skeleton variant="text" width={120} height={20} />
            </Flex>
        </Content>
    </Box>
);
